import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppConstants } from '../constants';
import { PreInitSettingsService } from './settings/pre-init-setting.service';

/**
 * One entry of the `POST sso/token/verify` response — the middleware relays back the same bare
 * array that was posted to `sso/token`. `claimNumber` is present when the EZ Pass link was minted
 * for a specific claim; for a general account link the key is ABSENT entirely (not `null`) —
 * callers should treat missing and empty-string the same way. `childCertificateNumbers` names the
 * child certificate(s) behind the claim, but not which product they belong to.
 */
export interface IEzPassVerifyEntry {
  dealerCode?: string;
  certificateNumber?: string;
  claimNumber?: string;
  childCertificateNumbers?: string[];
}

/** Bare array (current); a single wrapper-less object is still accepted from older middleware. */
export type IEzPassVerifyResponse = IEzPassVerifyEntry[] | IEzPassVerifyEntry;

@Injectable({ providedIn: 'root' })
export class EzPassService {
  private httpClient = inject(HttpClient);
  private preInitSettingsService = inject(PreInitSettingsService);

  private get verifyUrl(): string {
    const settings = this.preInitSettingsService.getSettings()?.middleWare;
    return `${settings?.middlewareUrl ?? ''}${settings?.accountPath ?? ''}${AppConstants.API_ENDPOINTS.VerifySessionToken}`;
  }

  /**
   * Exchanges an EZ Pass session token for an identity token.
   * The HTTP interceptor automatically captures the `IdentityToken` response header
   * and writes it to localStorage['identity-token'] on success.
   */
  verifySession(token: string): Observable<HttpResponse<IEzPassVerifyResponse>> {
    return this.httpClient.post<IEzPassVerifyResponse>(this.verifyUrl, { token }, { observe: 'response' });
  }

  /** First entry carrying a non-blank claimNumber, or null for a general (non-claim) link. */
  getClaimEntry(body: IEzPassVerifyResponse | null | undefined): IEzPassVerifyEntry | null {
    const entries = Array.isArray(body) ? body : body ? [body] : [];
    return entries.find((entry) => !!entry?.claimNumber?.trim()) ?? null;
  }
}
